import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API_BASE, formatErr, resolveMediaUrl } from "../lib/api";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Check, PhoneCall, X } from "lucide-react";

export default function PublicSocietyApproval() {
  const { visitorId } = useParams();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [visitor, setVisitor] = useState(null);
  const [latestCall, setLatestCall] = useState(null);
  const [remarks, setRemarks] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let active = true;
    async function load() {
      try {
        const res = await axios.get(`${API_BASE}/public/society/visitors/${visitorId}`, { params: { token } });
        if (active) setVisitor(res.data);
      } catch (err) {
        if (active) setMessage(formatErr(err, "Approval link is invalid or expired"));
      }
    }
    if (visitorId && token) load();
    else setMessage("Approval token is missing.");
    return () => { active = false; };
  }, [visitorId, token]);

  useEffect(() => {
    if (!visitorId || !token) return undefined;
    let active = true;
    async function loadLatestCall() {
      try {
        const res = await axios.get(`${API_BASE}/public/society/visitors/${visitorId}/call/latest`, { params: { token } });
        if (active) setLatestCall(res.data);
      } catch {
        if (active) setLatestCall(null);
      }
    }
    loadLatestCall();
    const timer = window.setInterval(loadLatestCall, 4000);
    return () => {
      active = false;
      window.clearInterval(timer);
    };
  }, [visitorId, token]);

  const submit = async (action) => {
    try {
      setBusy(true);
      const res = await axios.patch(`${API_BASE}/public/society/visitors/${visitorId}/${action}`, { remarks }, { params: { token } });
      setVisitor(res.data);
      setMessage(action === "approve" ? "Visitor approved." : "Visitor rejected.");
    } catch (err) {
      setMessage(formatErr(err, "Could not update visitor"));
    } finally {
      setBusy(false);
    }
  };

  const answerCall = async () => {
    try {
      setBusy(true);
      const res = latestCall?.id
        ? { data: latestCall }
        : await axios.get(`${API_BASE}/public/society/visitors/${visitorId}/call/latest`, { params: { token } });
      const callId = res.data?.id;
      if (!callId) {
        setMessage("No active guard call yet. Please ask the guard to start the video call.");
        return;
      }
      setMessage("Video calling is available only in the RP Society Android app. Please open the mobile app to answer this call.");
    } catch (err) {
      setMessage(formatErr(err, "No active guard call yet. Please ask the guard to start the video call."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] px-4 py-8 text-[#111827]">
      <div className="mx-auto max-w-xl rounded-sm border border-[#E5E7EB] bg-white p-6 shadow-sm">
        <div className="text-[11px] font-semibold uppercase tracking-[0.22em] text-[#6B7280]">RP Signage Society</div>
        <h1 className="mt-2 text-3xl font-extrabold">Visitor approval</h1>
        {message && <div className="mt-4 rounded-sm border border-[#BFDBFE] bg-[#EFF6FF] p-3 text-sm text-[#1D4ED8]">{message}</div>}
        {visitor && (
          <div className="mt-6 space-y-5">
            {visitor.visitorPhoto && <img src={resolveMediaUrl(visitor.visitorPhoto)} alt="" className="aspect-video w-full rounded-sm object-cover" />}
            <div>
              <div className="text-2xl font-extrabold">{visitor.visitorName}</div>
              <div className="mt-1 text-[#6B7280]">{visitor.purpose || "Visitor"} for {visitor.wing} {visitor.flatNumber}</div>
            </div>
            <div className="grid gap-3 rounded-sm bg-[#F8FAFC] p-4 text-sm sm:grid-cols-2">
              <div><span className="text-[#6B7280]">Mobile:</span> {visitor.visitorMobile || "-"}</div>
              <div><span className="text-[#6B7280]">Owner:</span> {visitor.ownerName || "-"}</div>
              <div><span className="text-[#6B7280]">Gate:</span> {visitor.gateName || "-"}</div>
              <div><span className="text-[#6B7280]">Status:</span> {visitor.status || "pending"}</div>
            </div>
            <Textarea value={remarks} onChange={(e) => setRemarks(e.target.value)} placeholder="Remarks optional" />
            <div className="grid gap-2 sm:grid-cols-3">
              <Button disabled={busy || visitor.status !== "pending"} onClick={() => submit("approve")} className="min-h-11 bg-emerald-600 text-white hover:bg-emerald-700"><Check className="mr-2 h-4 w-4" /> Approve</Button>
              <Button disabled={busy || visitor.status !== "pending"} variant="outline" onClick={() => submit("reject")} className="min-h-11"><X className="mr-2 h-4 w-4 text-red-600" /> Reject</Button>
              <Button disabled={busy} variant={latestCall ? "default" : "outline"} onClick={answerCall} className={`min-h-11 ${latestCall ? "bg-slate-950 text-white hover:bg-slate-800" : ""}`}><PhoneCall className="mr-2 h-4 w-4" /> {latestCall ? "Answer Call" : "Video Call"}</Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
